/**
 * **매장 주문접수 임시중단(R-E1) — 판정·문구 한 곳.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 서버가 저장하는 사실은 매장의 만료 시각 `pausedUntil` 하나다(ISO-8601 `+09:00`, 중단 중일 때만 값).
 * 「중단 중」은 저장하지 않고 **지금 시각과 견줘** 파생한다 — 서버 `StorePause.activeUntil` 과 같은 식
 * (`now < pausedUntil`, 만료 시각 정각에는 이미 풀려 있다). 그래서 만료를 푸는 요청이 없고, 화면은 시각이
 * 흐르면 다시 계산만 한다([storePauseRecheckIn] 이 다음 계산 시점을 준다).
 *
 * 권한은 **서버 액션**이 답한다([STORE_PAUSE_ACTION]) — 역할 이름을 적지 않는다. 관리점·운영사(R-E2)는
 * 서버 SQL 이 같은 URL 에 역할을 더하는 것만으로 같은 화면을 쓴다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/R-E1-임시중단-appserver-report.md` §2~§4,
 * 문구는 브리프 §1-3.
 */

// 확장자를 적는 이유: 단위 테스트가 이 파일을 node 에서 그대로 읽는다(node ESM 은 확장자를 추론하지 않는다)
import { formatKst } from './kstDate.js'

/**
 * 매장 계정 사이드 메뉴 「주문 임시중단」의 라우트 — appserver SQL 033 §3 이 `sys_resources` 에 넣은 값과 같아야
 * 메뉴 클릭이 빈 화면이 되지 않는다.
 */
export const STORE_PAUSE_PATH = '/v/business/store/pause'

/** 세션 액션 id — SQL 033 §2 의 `sys_action` 과 같은 문자열 */
export const STORE_PAUSE_ACTION = Object.freeze({
  /** `PUT /store/{storeCd}/pause` */
  PAUSE: 'StorePausePut',
  /** `DELETE /store/{storeCd}/pause` */
  RESUME: 'StorePauseDelete',
})

/** 길이의 간격과 상한(분) — 서버 `StorePause.Length` 와 같은 규칙(30분 간격, 8시간까지. 밖이면 400) */
const LENGTH_STEP_MINUTES = 30
const LENGTH_MAX_MINUTES = 8 * 60

/**
 * **분 → 「N분」「H시간」「H시간 M분」 — 시간 글 한 곳.** 60분 미만은 분만, 이상은 시간(몫)과 남은 분(나머지)이다.
 * 버튼 글(「1시간 30분」 — 길이만 적는다, 카드 제목 「주문 접수 임시중단」이 동사를 맡는다)과 중단 중 문장의 남은
 * 시간(「7시간 59분 남음」)이 같이 쓴다. POS2 Lite 와 같은 글이다.
 */
function durationText(minutes) {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60

  if (!hours) return `${rest}분`

  return rest ? `${hours}시간 ${rest}분` : `${hours}시간`
}

/**
 * 걸 수 있는 길이 — 30분부터 8시간까지 30분 간격 16개(위 간격·상한). 이미 중단 중에 다시 걸면 서버가
 * **지금부터** 새 길이로 바꾼다(누적 아님).
 */
export const STORE_PAUSE_LENGTHS = Object.freeze(
  Array.from({ length: LENGTH_MAX_MINUTES / LENGTH_STEP_MINUTES }, (_, index) => {
    const minutes = (index + 1) * LENGTH_STEP_MINUTES

    return Object.freeze({ minutes, label: durationText(minutes) })
  })
)

/** 화면 문구 — 브리프 §1-3 그대로(도움말은 2026-10-16 배달앱을 나열하지 않게 줄였다 — POS2 Lite 와 같은 문장) */
export const STORE_PAUSE_TEXT = Object.freeze({
  title: '주문 접수 임시중단',
  resume: '지금 해제',
  help: '오더엔 주문(주문웹·콜팝 앱)을 잠시 받지 않습니다. 배달앱 주문은 각 앱에서 따로 멈추세요.',
  /** 서버 문장이 없는 실패의 폴백 — 있으면 서버 문장을 그대로 쓴다(`httpErrorMessage`) */
  failed: '요청을 처리하지 못했습니다.',
  loadFailed: '매장 정보를 읽지 못했습니다.',
})

/** 걸기(`PUT`, 본문 `{minutes}`)·해제(`DELETE`) 공용 경로 */
export function storePauseUrl(storeCd) {
  return '/store/' + storeCd + '/pause'
}

/**
 * 걸기·해제 요청의 axios 옵트아웃 — 품절 화면의 `POS_SOLDOUT_REQUEST_CONFIG` 와 같은 모양이다.
 *
 * - `showErrorMessage:false` — **실패 문장은 카드 안에서 말한다**(인터셉터 토스트와 두 번 말하지 않게).
 * - `showLoading:false` — 진행은 누른 버튼이 보인다(`el-button` loading, 나머지 버튼은 잠김). 매장 상세 폼 위의
 *   작은 동작에 전체 화면 잠금을 씌우지 않는다.
 *
 * 403 로그아웃은 옵트아웃하지 않는다: 버튼은 액션을 가진 세션에만 그려지고, 범위 밖 매장은 서버가 404 로 답한다
 * — 그래도 403 이 오면 보안 계층이 세션을 끊은 것이라 되살릴 수 없다(`plugins/http/policy` `READ_DENIED`).
 */
export const STORE_PAUSE_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false, showLoading: false })

const MINUTE_MS = 60 * 1000

const NOT_PAUSED = Object.freeze({ paused: false, untilMs: null, untilText: '', minutesLeft: 0 })

/**
 * 순간(ms) → KST `HH:mm` — 매장 운영 시각은 한국 기준이다(서버 `StorePause.ZONE`). 보는 사람의 시간대와 무관하게 적는 규칙은
 * `kstDate` 한 곳이다
 */
const kstHourMinute = (ms) => formatKst('HH:mm', ms)

/**
 * **지금 중단 중인가 — 판정 한 곳.**
 *
 * - 값이 없거나 읽을 수 없으면 중단 아님(구 서버 응답에는 칸이 없다).
 * - `now >= pausedUntil` 이면 중단 아님 — 서버가 `pausedUntil` 을 주었더라도 그 사이 시간이 지났다.
 * - 남은 분은 **올림**이다: 방금 30분을 걸면 「30분 남음」, 마지막 1분 동안은 「1분 남음」(0분은 없다).
 *
 * @param {?string} pausedUntil 매장 응답·걸기 응답의 `pausedUntil`
 * @param {number} nowMs 지금(`Date.now()`) — 시계를 읽는 자리는 화면(경계)이다
 * @returns {{paused: boolean, untilMs: ?number, untilText: string, minutesLeft: number}}
 */
export function storePauseState(pausedUntil, nowMs) {
  const untilMs = typeof pausedUntil === 'string' && pausedUntil ? Date.parse(pausedUntil) : NaN

  if (!Number.isFinite(untilMs) || !(nowMs < untilMs)) return NOT_PAUSED

  return Object.freeze({
    paused: true,
    untilMs,
    untilText: kstHourMinute(untilMs),
    minutesLeft: Math.ceil((untilMs - nowMs) / MINUTE_MS),
  })
}

/**
 * 중단 중 문장 — 브리프 §1-3 「주문 접수 임시중단 중 — HH:mm 까지(N분 남음). 그 뒤 자동으로 다시 받습니다.」
 * 남은 시간은 버튼 글과 같은 규칙이다([durationText] — 「45분 남음」「8시간 남음」「7시간 59분 남음」).
 * 중단 아님이면 빈 문자열이다(그때 화면은 문장 대신 버튼과 도움말을 그린다).
 */
export function storePauseMessage(state) {
  if (!state || !state.paused) return ''

  const left = durationText(state.minutesLeft)

  return `주문 접수 임시중단 중 — ${state.untilText} 까지(${left} 남음). 그 뒤 자동으로 다시 받습니다.`
}

/**
 * **다음에 다시 계산할 때까지(ms)** — 남은 분이 바뀌는 순간이고, 마지막 회차는 곧 만료 순간이다.
 *
 * 1분 간격을 고정하지 않는 이유: 고정 간격이면 만료 뒤 최대 1분 동안 「1분 남음」이 남는다. 남은 시간의 분
 * 경계에 맞추면 간격은 언제나 1분 이하이고 만료되는 그 순간 「아님」이 된다. 중단 아님이면 `null`(시계 불필요).
 *
 * @param {{paused: boolean, untilMs: ?number}} state [storePauseState] 의 결과
 * @param {number} nowMs 그 결과를 만든 지금
 * @returns {?number}
 */
export function storePauseRecheckIn(state, nowMs) {
  if (!state || !state.paused) return null

  return (state.untilMs - nowMs) % MINUTE_MS || MINUTE_MS
}
